import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';
import AppsIcon from '@mui/icons-material/Apps';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import CloudIcon from '@mui/icons-material/Cloud';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HandshakeIcon from '@mui/icons-material/Handshake';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import InventoryIcon from '@mui/icons-material/Inventory';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PublicIcon from '@mui/icons-material/Public';
import RepeatIcon from '@mui/icons-material/Repeat';
import SourceIcon from '@mui/icons-material/Source';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WorkIcon from '@mui/icons-material/Work';
import {
  asArray,
  BRAND_BLUE,
  DARK_TEXT,
  EmptyState,
  formatNumber,
  hasMeaningfulValue,
  pick,
  SectionCard,
  shellCardSx,
  SLATE_TEXT,
  SUCCESS_GREEN,
  WARNING_AMBER,
} from './osintCommon';

const FULL_WIDTH_SOCIAL_PLATFORMS = new Set(['linkedin', 'facebook', 'twitter', 'x']);
const RELATIONSHIP_PAGE_SIZE = 4;
const US_STATES_GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const WORLD_GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

export const isEmptyValue = (value) => !hasMeaningfulValue(value);

export const formatLabel = (key) =>
  String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

export const formatDate = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return String(date);
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export const truncateText = (text, maxLength = 180) => {
  const value = String(text || '');
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(0, maxLength - 1))}...`;
};

export const isUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());

export const getCountryFlag = (countryCode) => {
  const code = String(countryCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return code
    .split('')
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('');
};

export const formatAddress = (location = {}) => {
  if (typeof location === 'string') return location;
  const lineParts = [location.line1, location.line2].filter(Boolean);
  const cityLine = [
    location.city,
    location.state,
    location.postal_code || location.postalCode,
  ].filter(Boolean).join(', ').replace(/, ([^,]*)$/, ' $1');
  const country = location.country || '';
  return [lineParts.join(', '), cityLine, country].filter(Boolean).join('\n');
};

const getLocationCoordinates = (location = {}) => {
  if (!location || typeof location !== 'object') return null;
  const coordinates = location.geo_coordinates || location.geoCoordinates || location.coordinates || location.coords || {};
  const latitude = Number(location.latitude ?? location.lat ?? coordinates.latitude ?? coordinates.lat);
  const longitude = Number(location.longitude ?? location.lng ?? location.lon ?? coordinates.longitude ?? coordinates.lng ?? coordinates.lon);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
};

const approximateLocationCoordinates = (location = {}) => {
  if (!location || typeof location !== 'object') return null;
  const city = String(location.city || '').trim().toLowerCase();
  const state = String(location.state || location.region || '').trim().toLowerCase();
  const country = String(location.country || '').trim().toUpperCase();

  if (country !== 'US' && country !== 'USA' && country !== 'UNITED STATES') return null;
  if (city.includes('huntsville') && state.includes('alabama')) return { latitude: 34.7304, longitude: -86.5861 };

  const stateCentroids = {
    alabama: { latitude: 32.8067, longitude: -86.7911 },
    california: { latitude: 36.1162, longitude: -119.6816 },
    florida: { latitude: 27.7663, longitude: -81.6868 },
    georgia: { latitude: 33.0406, longitude: -83.6431 },
    'new york': { latitude: 42.1657, longitude: -74.9481 },
    texas: { latitude: 31.0545, longitude: -97.5635 },
    washington: { latitude: 47.4009, longitude: -121.4905 },
  };

  return stateCentroids[state] || null;
};

const companyLocationCandidates = (company = {}, explicitHeadquarters) => {
  const socialLocations = Object.values(pick(company, ['social_media', 'socialMedia', 'socials'], {}))
    .flatMap((platform) => {
      const profile = platform?.profile || platform || {};
      const basic = profile.basic_info || profile.basicInfo || profile;
      const locationData = basic.locations || profile.locations || {};
      return [
        locationData.headquarters,
        ...asArray(locationData.offices),
      ];
    })
    .filter(hasMeaningfulValue);

  return [
    explicitHeadquarters,
    ...asArray(pick(company, ['locations', 'offices'], [])),
    ...socialLocations,
  ].filter(hasMeaningfulValue);
};

const bestHeadquartersLocation = (company = {}, explicitHeadquarters) => {
  const candidates = companyLocationCandidates(company, explicitHeadquarters);
  return (
    candidates.find((location) => getLocationCoordinates(location) && (location.is_hq || location.isHq || location.description === 'Headquarters')) ||
    candidates.find((location) => getLocationCoordinates(location)) ||
    explicitHeadquarters ||
    candidates[0] ||
    ''
  );
};

export const getRelationshipType = (description = '') => {
  const text = String(description || '').toLowerCase();
  if (text.includes('customer') || text.includes('client')) return 'Customer';
  if (text.includes('partner') || text.includes('partnership')) return 'Partner';
  if (text.includes('distributor')) return 'Distributor';
  if (text.includes('msp') || text.includes('managed service')) return 'MSP';
  if (text.includes('integration') || text.includes('integrates')) return 'Integration';
  return 'Unknown';
};

const relationshipTypeForItem = (item, fallback = '') => {
  if (!item || typeof item !== 'object') return fallback || 'Unknown';
  const explicitType = scalarText(pick(item, ['relationship_type', 'relationshipType', 'type', 'category'], ''));
  const inferredType = getRelationshipType([
    explicitType,
    valueTitle(item, ''),
    valueDescription(item),
  ].filter(Boolean).join(' '));
  return inferredType !== 'Unknown' ? inferredType : fallback || explicitType || 'Unknown';
};

const scalarText = (value) => {
  if (isEmptyValue(value)) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

const objectEntries = (object) =>
  Object.entries(object || {}).filter(([, value]) => hasMeaningfulValue(value));

const fieldCardSx = {
  p: 1.5,
  height: '100%',
  minHeight: 138,
  borderRadius: 3,
  border: `1px solid ${alpha(BRAND_BLUE, 0.1)}`,
  bgcolor: alpha(BRAND_BLUE, 0.025),
  overflow: 'hidden',
};

const valueTitle = (item, fallback = 'Untitled') => {
  if (typeof item !== 'object' || !item) return scalarText(item) || fallback;
  return (
    pick(item, ['name', 'title', 'company', 'full_name', 'fullName', 'headline'], '') ||
    [item.first_name, item.last_name].filter(Boolean).join(' ') ||
    fallback
  );
};

const valueDescription = (item) => {
  if (typeof item !== 'object' || !item) return '';
  return pick(item, ['description', 'summary', 'snippet', 'bio', 'details'], '');
};

const lineClampSx = (lines = 2) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

const normalizeKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export const dedupeLocations = (locations) => {
  const byKey = new Map();
  asArray(locations)
    .filter(hasMeaningfulValue)
    .forEach((location) => {
      const address = typeof location === 'string' ? location : formatAddress(location);
      const key = normalizeKey(address || [
        location?.line1,
        location?.line2,
        location?.city,
        location?.state,
        location?.postal_code || location?.postalCode,
        location?.country,
      ].filter(Boolean).join('|'));

      if (!key) return;
      const existing = byKey.get(key);
      const isHq = !!(location?.is_hq || location?.isHq);
      const existingIsHq = !!(existing?.is_hq || existing?.isHq);
      if (!existing || (isHq && !existingIsHq)) byKey.set(key, location);
    });
  return Array.from(byKey.values());
};

const profileBase = (profile) => {
  if (!profile || typeof profile !== 'object') return {};
  return profile.basic_info || profile.basicInfo || profile;
};

const profileStats = (profile) => {
  const base = profileBase(profile);
  return profile.stats || base.stats || {};
};

const profileDescription = (profile) => {
  const base = profileBase(profile);
  return pick(base, ['description', 'tagline', 'headline', 'about', 'bio'], '');
};

const profileSummaryFields = (profile) => {
  const base = profileBase(profile);
  const stats = profileStats(profile);
  const fields = [
    ['Name', pick(base, ['name', 'company_name', 'companyName', 'universal_name', 'universalName'], '')],
    ['Website', pick(base, ['website', 'website_url', 'websiteUrl'], '')],
    ['Industry', pick(base, ['industry', 'industries'], '')],
    ['Founded', pick(base, ['founded_year', 'foundedYear', 'founded'], pick(base.founded_info, ['year'], ''))],
    ['Followers', pick(stats, ['follower_count', 'followers', 'followerCount'], '')],
    ['Employees', pick(stats, ['employee_count', 'employeeCount'], pick(base, ['employee_count', 'employeeCount'], ''))],
  ];
  return fields.filter(([, value]) => hasMeaningfulValue(value));
};

const socialPlatformData = (platform, value) => {
  const normalizedPlatform = String(platform || '').toLowerCase();
  const profile = typeof value === 'object' && !Array.isArray(value) ? (value.profile || {}) : {};
  const posts = typeof value === 'object' && !Array.isArray(value) ? asArray(pick(value, ['posts', 'recent_posts', 'recentPosts'], [])) : [];
  const description = profileDescription(profile);
  const url = socialUrl(value);
  const fields = profileSummaryFields(profile);
  const hasDeepProfile = FULL_WIDTH_SOCIAL_PLATFORMS.has(normalizedPlatform) || (hasMeaningfulValue(profile) && (fields.length > 2 || description.length > 140));
  return { profile, posts, description, url, fields, hasDeepProfile, normalizedPlatform };
};

export const SourceBadges = ({ refs }) => {
  const sources = asArray(refs).filter(hasMeaningfulValue);
  if (!sources.length) return null;

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
      {sources.slice(0, 8).map((ref, index) => {
        const provider = typeof ref === 'object' ? (ref.provider || ref.source || 'source') : String(ref);
        const path = typeof ref === 'object' ? (ref.path || ref.url || '') : '';
        return (
          <Chip
            key={`${provider}-${path}-${index}`}
            size="small"
            icon={<SourceIcon fontSize="small" />}
            label={path ? `${provider}: ${path}` : provider}
            sx={{ maxWidth: 280, fontWeight: 800, color: BRAND_BLUE, bgcolor: alpha(BRAND_BLUE, 0.07), '& .MuiChip-icon': { color: BRAND_BLUE } }}
          />
        );
      })}
    </Stack>
  );
};

const StructuredValue = ({ value }) => {
  if (isEmptyValue(value)) return null;

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return isUrl(value) ? (
      <Link href={String(value)} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
        {String(value)}
      </Link>
    ) : (
      <Typography variant="body2" sx={{ color: SLATE_TEXT, lineHeight: 1.65, wordBreak: 'break-word' }}>
        {String(value)}
      </Typography>
    );
  }

  if (Array.isArray(value)) {
    return (
      <Stack spacing={1}>
        {value.filter(hasMeaningfulValue).map((item, index) => (
          <Box key={index} sx={{ p: 1.2, borderRadius: 2.5, border: `1px solid ${alpha(BRAND_BLUE, 0.1)}`, bgcolor: alpha(BRAND_BLUE, 0.025) }}>
            <StructuredValue value={item} />
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Grid container spacing={1}>
      {objectEntries(value).map(([key, entry]) => (
        <Grid item xs={12} sm={6} key={key}>
          <Box sx={{ p: 1.2, borderRadius: 2.5, border: `1px solid ${alpha(BRAND_BLUE, 0.08)}`, bgcolor: '#ffffff' }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, color: BRAND_BLUE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {formatLabel(key)}
            </Typography>
            <Box sx={{ mt: 0.6 }}>
              <StructuredValue value={entry} />
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

export const DetailDrawer = ({ open, onClose, title, subtitle, data }) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{ sx: { width: { xs: '100%', sm: 640 }, bgcolor: '#F7FAFE' } }}
  >
    <Box sx={{ p: 2.5, bgcolor: BRAND_BLUE, color: '#ffffff' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar sx={{ bgcolor: alpha('#ffffff', 0.16), color: '#ffffff' }}><BusinessIcon /></Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>{title || 'Details'}</Typography>
          {subtitle ? <Typography sx={{ mt: 0.4, color: alpha('#ffffff', 0.85) }}>{subtitle}</Typography> : null}
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#ffffff' }}><CloseIcon /></IconButton>
      </Stack>
    </Box>
    <Box sx={{ p: 2.5 }}>
      <StructuredValue value={data} />
      <SourceBadges refs={pick(data, ['source_refs', 'sourceRefs', 'sources'], [])} />
    </Box>
  </Drawer>
);

const DetailButton = ({ item, title, subtitle }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 800 }}>
        View details
      </Button>
      <DetailDrawer open={open} onClose={() => setOpen(false)} title={title} subtitle={subtitle} data={item} />
    </>
  );
};

const countryName = (country) => {
  const code = String(country || '').trim().toUpperCase();
  if (code === 'US' || code === 'USA' || code === 'UNITED STATES') return 'United States';
  if (!country) return 'Country context';
  return String(country);
};

const locatorPosition = (location, coordinates) => {
  const country = String(location?.country || '').trim().toUpperCase();
  if (country === 'US' || country === 'USA' || country === 'UNITED STATES') {
    const lon = coordinates?.longitude;
    const lat = coordinates?.latitude;
    if (Number.isFinite(lon) && Number.isFinite(lat)) {
      return {
        left: `${Math.min(Math.max(((lon + 125) / 59) * 100, 10), 90)}%`,
        top: `${Math.min(Math.max(((50 - lat) / 26) * 100, 14), 86)}%`,
      };
    }

    const state = String(location?.state || '').toLowerCase();
    if (state.includes('alabama')) return { left: '72%', top: '68%' };
    if (state.includes('california')) return { left: '13%', top: '58%' };
    if (state.includes('new york')) return { left: '84%', top: '36%' };
    if (state.includes('texas')) return { left: '48%', top: '76%' };
    if (state.includes('florida')) return { left: '80%', top: '82%' };
    return { left: '52%', top: '54%' };
  }

  if (coordinates) {
    return {
      left: `${Math.min(Math.max(((coordinates.longitude + 180) / 360) * 100, 8), 92)}%`,
      top: `${Math.min(Math.max(((80 - coordinates.latitude) / 145) * 100, 10), 88)}%`,
    };
  }

  return { left: '52%', top: '52%' };
};

const CountryLocatorGraphic = ({ location, coordinates }) => {
  const country = countryName(location?.country);
  const isUnitedStates = country === 'United States';
  const markerCoordinates = coordinates ? [coordinates.longitude, coordinates.latitude] : null;
  const geographyUrl = isUnitedStates ? US_STATES_GEO_URL : WORLD_GEO_URL;
  const projection = isUnitedStates ? 'geoAlbersUsa' : 'geoEqualEarth';
  const position = locatorPosition(location, coordinates);
  const projectionConfig = isUnitedStates
    ? { scale: 760 }
    : { scale: 145, center: markerCoordinates || [0, 20] };
  const label = [location?.city, location?.state || location?.region].filter(Boolean).join(', ') || country;

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: 330, md: 390 },
        overflow: 'hidden',
        borderRadius: 3,
        border: `1px solid ${alpha(BRAND_BLUE, 0.1)}`,
        background: `linear-gradient(135deg, #E7F2FF 0%, #F8FBFF 48%, ${alpha(SUCCESS_GREEN, 0.16)} 100%)`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.8,
          backgroundImage: `
            linear-gradient(${alpha(BRAND_BLUE, 0.08)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(BRAND_BLUE, 0.08)} 1px, transparent 1px)
          `,
          backgroundSize: '44px 44px',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: { xs: 4, md: 8 },
          opacity: 0.95,
          '& svg': {
            display: 'block',
            width: '100%',
            height: '100%',
          },
        }}
      >
        <ComposableMap
          width={980}
          height={390}
          projection={projection}
          projectionConfig={projectionConfig}
          style={{ width: '100%', height: '100%' }}
        >
          <Geographies geography={geographyUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={alpha('#ffffff', 0.8)}
                  stroke={alpha(BRAND_BLUE, 0.36)}
                  strokeWidth={0.65}
                  style={{
                    default: { outline: 'none' },
                    hover: { outline: 'none', fill: alpha('#ffffff', 0.9) },
                    pressed: { outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>
          {markerCoordinates ? (
            <Marker coordinates={markerCoordinates}>
              <circle r={7} fill={BRAND_BLUE} stroke="#ffffff" strokeWidth={3} />
              <circle r={17} fill="none" stroke={alpha(BRAND_BLUE, 0.36)} strokeWidth={3} />
            </Marker>
          ) : null}
        </ComposableMap>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          inset: { xs: 22, md: 30 },
          borderRadius: 5,
          bgcolor: alpha('#ffffff', 0.08),
          border: `1px solid ${alpha('#ffffff', 0.46)}`,
          pointerEvents: 'none',
        }}
      />

      <Typography
        sx={{
          position: 'absolute',
          top: { xs: 24, md: 28 },
          left: { xs: 24, md: 30 },
          fontSize: { xs: '1.1rem', md: '1.4rem' },
          fontWeight: 950,
          color: DARK_TEXT,
          letterSpacing: '-0.02em',
        }}
      >
        {country}
      </Typography>
      <Typography
        sx={{
          position: 'absolute',
          top: { xs: 52, md: 64 },
          left: { xs: 24, md: 30 },
          fontSize: '0.75rem',
          fontWeight: 900,
          color: BRAND_BLUE,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        Country-level locator
      </Typography>

      <Box
        sx={{
          position: 'absolute',
          left: position.left,
          top: position.top,
          transform: 'translate(-50%, -100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 2,
        }}
      >
        <Avatar
          sx={{
            width: 54,
            height: 54,
            bgcolor: BRAND_BLUE,
            color: '#ffffff',
            boxShadow: `0 16px 32px ${alpha(BRAND_BLUE, 0.34)}`,
            border: '3px solid #ffffff',
          }}
        >
          <LocationOnIcon />
        </Avatar>
        <Box
          sx={{
            width: 12,
            height: 12,
            bgcolor: BRAND_BLUE,
            transform: 'rotate(45deg)',
            mt: '-9px',
            borderRight: '3px solid #ffffff',
            borderBottom: '3px solid #ffffff',
          }}
        />
      </Box>

      <Chip
        label={label}
        sx={{
          position: 'absolute',
          left: position.left,
          top: position.top,
          transform: 'translate(-50%, 24px)',
          bgcolor: alpha('#ffffff', 0.95),
          color: DARK_TEXT,
          fontWeight: 900,
          boxShadow: `0 8px 18px ${alpha(BRAND_BLUE, 0.16)}`,
          zIndex: 3,
        }}
      />
    </Box>
  );
};

export const HeadquartersLocationSection = ({ company }) => {
  const explicitHeadquarters = pick(company, ['hq', 'headquarters', 'headquarter'], '');
  const headquarters = bestHeadquartersLocation(company, explicitHeadquarters);
  if (!hasMeaningfulValue(headquarters)) return null;

  const address = formatAddress(headquarters) || scalarText(headquarters);
  if (!address) return null;

  const coordinates = getLocationCoordinates(headquarters);
  const locatorCoordinates = coordinates || approximateLocationCoordinates(headquarters);
  const mapQuery = coordinates
    ? `${coordinates.latitude},${coordinates.longitude}`
    : address.replace(/\n/g, ' ');
  const searchUrl = mapQuery ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}` : '';
  const context = typeof headquarters === 'object'
    ? [
      headquarters.city,
      headquarters.state || headquarters.region,
      headquarters.country,
    ].filter(Boolean).join(', ')
    : '';

  return (
    <SectionCard eyebrow="Headquarters Location" title="Country-level location context">
      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <CountryLocatorGraphic location={headquarters} coordinates={locatorCoordinates} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%', borderRadius: 3, border: `1px solid ${alpha(BRAND_BLUE, 0.12)}`, boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.2, height: '100%' }}>
              <Stack spacing={1.5} sx={{ height: '100%' }}>
                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                  <Avatar sx={{ width: 42, height: 42, bgcolor: alpha(BRAND_BLUE, 0.1), color: BRAND_BLUE }}>
                    <LocationOnIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: BRAND_BLUE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Headquarters
                    </Typography>
                    <Typography sx={{ mt: 0.3, color: DARK_TEXT, fontWeight: 950, lineHeight: 1.35 }}>
                      {context || 'Primary location'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: SLATE_TEXT, whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                      {address}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ p: 1.4, borderRadius: 3, bgcolor: alpha(BRAND_BLUE, 0.045), border: `1px solid ${alpha(BRAND_BLUE, 0.08)}` }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: BRAND_BLUE, textTransform: 'uppercase' }}>
                    View level
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.6, color: DARK_TEXT, fontWeight: 850 }}>
                    Country-level context
                  </Typography>
                  {coordinates ? (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: SLATE_TEXT }}>
                      Coordinates: {coordinates.latitude.toFixed(5)}, {coordinates.longitude.toFixed(5)}
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: SLATE_TEXT }}>
                      Coordinates were not returned; locator is based on address fields.
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 'auto' }}>
                  {typeof headquarters === 'object' && !Array.isArray(headquarters) ? (
                    <DetailButton item={headquarters} title="Headquarters" subtitle="Company profile location" />
                  ) : null}
                  {searchUrl ? (
                    <Button
                      href={searchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      startIcon={<MapIcon />}
                      sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900 }}
                    >
                      Open Maps
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </SectionCard>
  );
};

export const CompanyOverview = ({ company, metrics = [] }) => {
  const fields = [
    ['Website', pick(company, ['website', 'url'], '')],
  ].filter(([, value]) => hasMeaningfulValue(value));
  const companyName = pick(company, ['name', 'company_name', 'companyName'], 'Company profile');
  const legalName = pick(company, ['legal_name', 'legalName'], '');
  const summary = pick(company, ['description', 'summary', 'overview'], 'No company description was returned.');

  const displayField = (label, value) => {
    if (label === 'Headquarters' && typeof value === 'object') return formatAddress(value) || valueTitle(value, 'Headquarters');
    if (isUrl(value)) return value;
    if (typeof value === 'object') return valueTitle(value, formatAddress(value) || 'View details');
    return scalarText(value);
  };

  return (
    <SectionCard eyebrow="Company Overview" title={companyName} description={legalName && legalName !== companyName ? legalName : undefined}>
      <Grid container spacing={2.5} alignItems="stretch">
        <Grid item xs={12} lg={7}>
          <Box
            sx={{
              height: '100%',
              p: { xs: 1.4, md: 1.8 },
              borderRadius: 3,
              border: `1px solid ${alpha(BRAND_BLUE, 0.08)}`,
              bgcolor: alpha(BRAND_BLUE, 0.02),
            }}
          >
            <Typography variant="body1" sx={{ color: SLATE_TEXT, lineHeight: 1.9 }}>
              {summary}
            </Typography>
            {fields.length ? (
              <Grid container spacing={1.3} sx={{ mt: 1.5 }}>
                {fields.map(([label, value]) => (
                  <Grid item xs={12} md={7} key={label}>
                    <Box sx={{ ...fieldCardSx, minHeight: 86, p: 1.3, bgcolor: '#ffffff' }}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, color: BRAND_BLUE, textTransform: 'uppercase' }}>{label}</Typography>
                      {isUrl(value) ? (
                        <Link href={String(value)} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', mt: 0.6, fontWeight: 900, wordBreak: 'break-word' }}>
                          {String(value)}
                        </Link>
                      ) : (
                        <Typography sx={{ mt: 0.6, fontWeight: 900, color: DARK_TEXT, lineHeight: 1.55, wordBreak: 'break-word' }}>
                          {displayField(label, value)}
                        </Typography>
                      )}
                      {typeof value === 'object' && !Array.isArray(value) ? (
                        <Box sx={{ mt: 1 }}>
                          <DetailButton item={value} title={label} subtitle="Company profile field" />
                        </Box>
                      ) : null}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : null}
          </Box>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Grid container spacing={1.2} sx={{ height: '100%' }}>
            {metrics.map(({ label, value, helper, Icon, accent = BRAND_BLUE }) => (
              <Grid item xs={12} sm={6} key={label}>
                <Card
                  sx={{
                    height: '100%',
                    minHeight: 112,
                    borderRadius: 3,
                    border: `1px solid ${alpha(accent, 0.13)}`,
                    borderTop: `3px solid ${accent}`,
                    boxShadow: 'none',
                    bgcolor: '#ffffff',
                  }}
                >
                  <CardContent sx={{ p: 1.45 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(accent, 0.1), color: accent }}>
                        <Icon sx={{ fontSize: 19 }} />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: '0.66rem', fontWeight: 900, letterSpacing: '0.07em', textTransform: 'uppercase', color: BRAND_BLUE }}>
                          {label}
                        </Typography>
                        <Typography sx={{ mt: 0.25, fontSize: '1.25rem', fontWeight: 950, color: DARK_TEXT, lineHeight: 1.05, wordBreak: 'break-word' }}>
                          {value}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.55, color: SLATE_TEXT, lineHeight: 1.45, ...lineClampSx(2) }}>
                          {helper}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </SectionCard>
  );
};

export const LocationCards = ({ locations }) => {
  const rows = dedupeLocations(locations);
  if (!rows.length) return <EmptyState title="No locations returned" description="Known office and headquarters locations will appear here when available." />;

  return (
    <Grid container spacing={1.5}>
      {rows.map((location, index) => {
        const isHq = !!(location.is_hq || location.isHq);
        const city = [location.city, location.state].filter(Boolean).join(', ');
        const country = location.country || '';
        const address = formatAddress(location);
        const mapsUrl = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.replace(/\n/g, ' '))}` : '';
        return (
          <Grid item xs={12} md={6} key={`${address}-${index}`}>
            <Card sx={{ height: '100%', borderRadius: 3, border: `1px solid ${alpha(BRAND_BLUE, 0.12)}`, boxShadow: 'none' }}>
              <CardContent sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                  <Avatar sx={{ bgcolor: alpha(BRAND_BLUE, 0.1), color: BRAND_BLUE }}><LocationOnIcon /></Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                      <Typography sx={{ fontWeight: 900, color: DARK_TEXT }}>
                        {[getCountryFlag(country), city || country || 'Location'].filter(Boolean).join(' ')}
                      </Typography>
                      {isHq ? <Chip size="small" label="HQ" sx={{ fontWeight: 900, bgcolor: alpha(SUCCESS_GREEN, 0.1), color: SUCCESS_GREEN }} /> : null}
                    </Stack>
                    {address ? <Typography variant="body2" sx={{ mt: 0.8, color: SLATE_TEXT, whiteSpace: 'pre-line', lineHeight: 1.7 }}>{address}</Typography> : null}
                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                      {location.region ? <Chip size="small" label={location.region} /> : null}
                      {location.description ? <Chip size="small" label={location.description} /> : null}
                    </Stack>
                    {mapsUrl ? (
                      <Button href={mapsUrl} target="_blank" rel="noopener noreferrer" size="small" startIcon={<MapIcon />} sx={{ mt: 1.2, textTransform: 'none', fontWeight: 800 }}>
                        Open in Maps
                      </Button>
                    ) : null}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

const ProductCard = ({ item, category, icon, accent }) => {
  const name = valueTitle(item, category);
  const description = valueDescription(item);
  const url = typeof item === 'object' ? pick(item, ['url', 'website', 'link'], '') : '';
  return (
    <Card sx={{ height: '100%', borderRadius: 3, border: `1px solid ${alpha(accent, 0.14)}`, boxShadow: 'none' }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Avatar sx={{ bgcolor: alpha(accent, 0.1), color: accent }}>{icon}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Chip size="small" label={category} sx={{ mb: 1, fontWeight: 900, color: accent, bgcolor: alpha(accent, 0.08) }} />
            <Typography sx={{ fontWeight: 900, color: DARK_TEXT, wordBreak: 'break-word' }}>{name}</Typography>
            {description ? <Typography variant="body2" sx={{ mt: 0.8, color: SLATE_TEXT, lineHeight: 1.7 }}>{truncateText(description, 180)}</Typography> : null}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.4 }}>
              {url ? <Button href={url} target="_blank" rel="noopener noreferrer" size="small" endIcon={<OpenInNewIcon />} variant="contained" sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 800 }}>Open URL</Button> : null}
              <DetailButton item={item} title={name} subtitle={category} />
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ProductGrid = ({ title, description, items, category, icon, accent }) => {
  const rows = asArray(items).filter(hasMeaningfulValue);
  return (
    <SectionCard eyebrow="Products & Services" title={title} description={description}>
      {rows.length ? (
        <Grid container spacing={1.5}>
          {rows.map((item, index) => (
            <Grid item xs={12} md={6} key={`${valueTitle(item, category)}-${index}`}>
              <ProductCard item={item} category={category} icon={icon} accent={accent} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyState title={`No ${title.toLowerCase()} returned`} />
      )}
    </SectionCard>
  );
};

export const ProductCards = ({ products }) => (
  <Grid container spacing={2.5}>
    <Grid item xs={12} lg={6}><ProductGrid title="SaaS Products" description="Cloud and application products connected to the company." items={pick(products, ['saas_products', 'saasProducts'], [])} category="SaaS Product" icon={<CloudIcon />} accent={BRAND_BLUE} /></Grid>
    <Grid item xs={12} lg={6}><ProductGrid title="Services" description="Publicly listed service offerings." items={pick(products, ['services'], [])} category="Service" icon={<MiscellaneousServicesIcon />} accent={SUCCESS_GREEN} /></Grid>
    <Grid item xs={12} lg={6}><ProductGrid title="Mobile Apps" description="Mobile applications associated with the company." items={pick(products, ['mobile_apps', 'mobileApps'], [])} category="Mobile App" icon={<AppsIcon />} accent="#7C3AED" /></Grid>
    <Grid item xs={12} lg={6}><ProductGrid title="Physical Products" description="Physical product references returned by OSINT." items={pick(products, ['physical_products', 'physicalProducts'], [])} category="Physical Product" icon={<InventoryIcon />} accent={WARNING_AMBER} /></Grid>
  </Grid>
);

const RelationshipCard = ({ item, fallbackType, accent }) => {
  const name = valueTitle(item, 'Unknown relationship');
  const description = valueDescription(item);
  const type = relationshipTypeForItem(item, fallbackType);
  const person = [item?.first_name, item?.last_name].filter(Boolean).join(' ');
  return (
    <Card sx={{ height: '100%', borderRadius: 3, border: `1px solid ${alpha(accent, 0.14)}`, boxShadow: 'none' }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Avatar sx={{ bgcolor: alpha(accent, 0.1), color: accent }}><HandshakeIcon /></Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Chip size="small" label={type} sx={{ mb: 1, fontWeight: 900, color: accent, bgcolor: alpha(accent, 0.08) }} />
            <Typography sx={{ fontWeight: 900, color: DARK_TEXT, wordBreak: 'break-word' }}>{name}</Typography>
            {person || item?.job ? <Typography variant="body2" sx={{ mt: 0.35, color: SLATE_TEXT }}>{[person, item?.job].filter(Boolean).join(' | ')}</Typography> : null}
            {description ? <Typography variant="body2" sx={{ mt: 0.8, color: SLATE_TEXT, lineHeight: 1.7, ...lineClampSx(3) }}>{truncateText(description, 240)}</Typography> : null}
            <SourceBadges refs={pick(item, ['source_refs', 'sourceRefs', 'sources'], [])} />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const RelationshipGroup = ({ title, items, type, accent }) => {
  const rows = asArray(items).filter(hasMeaningfulValue);
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / RELATIONSHIP_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleRows = rows.slice(currentPage * RELATIONSHIP_PAGE_SIZE, currentPage * RELATIONSHIP_PAGE_SIZE + RELATIONSHIP_PAGE_SIZE);

  return (
    <SectionCard
      eyebrow="Relationships"
      title={title}
      description={rows.length > RELATIONSHIP_PAGE_SIZE ? `Showing ${currentPage * RELATIONSHIP_PAGE_SIZE + 1}-${Math.min(rows.length, (currentPage + 1) * RELATIONSHIP_PAGE_SIZE)} of ${rows.length}` : undefined}
      action={rows.length > RELATIONSHIP_PAGE_SIZE ? (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            disabled={currentPage === 0}
            onClick={() => setPage((previous) => Math.max(0, previous - 1))}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900 }}
          >
            Prev
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={currentPage >= pageCount - 1}
            onClick={() => setPage((previous) => Math.min(pageCount - 1, previous + 1))}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900 }}
          >
            Next
          </Button>
        </Stack>
      ) : null}
    >
      {rows.length ? (
        <Grid container spacing={1.5}>
          {visibleRows.map((item, index) => (
            <Grid item xs={12} md={6} key={`${valueTitle(item, title)}-${index}`}>
              <RelationshipCard item={item} fallbackType={type} accent={accent} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyState title={`No ${title.toLowerCase()} returned`} />
      )}
    </SectionCard>
  );
};

const dedupeRelationships = (items) => {
  const seen = new Set();
  return asArray(items)
    .filter(hasMeaningfulValue)
    .filter((item) => {
      const key = typeof item === 'object'
        ? JSON.stringify({
          name: valueTitle(item, ''),
          description: valueDescription(item),
          company: item.company || '',
        }).toLowerCase()
        : String(item).toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const groupRelationshipsByType = (relationships = {}) => {
  const grouped = {
    customers: dedupeRelationships(pick(relationships, ['customers'], [])),
    partners: dedupeRelationships(pick(relationships, ['partners'], [])),
    other: [],
  };

  dedupeRelationships(pick(relationships, ['other_relationships', 'otherRelationships', 'relationships'], []))
    .forEach((item) => {
      const type = relationshipTypeForItem(item);
      if (type === 'Customer') grouped.customers.push(item);
      else if (type === 'Partner') grouped.partners.push(item);
      else grouped.other.push(item);
    });

  return {
    customers: dedupeRelationships(grouped.customers),
    partners: dedupeRelationships(grouped.partners),
    other: dedupeRelationships(grouped.other),
  };
};

export const RelationshipCards = ({ relationships }) => {
  const grouped = groupRelationshipsByType(relationships);

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} lg={4}><RelationshipGroup title="Customers" items={grouped.customers} type="Customer" accent={SUCCESS_GREEN} /></Grid>
      <Grid item xs={12} lg={4}><RelationshipGroup title="Partners" items={grouped.partners} type="Partner" accent={WARNING_AMBER} /></Grid>
      <Grid item xs={12} lg={4}><RelationshipGroup title="Other Relationships" items={grouped.other} accent="#7C3AED" /></Grid>
    </Grid>
  );
};

const socialUrl = (value) => {
  if (!hasMeaningfulValue(value)) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(socialUrl).find(Boolean) || '';
  return value.url || value.link || value.profile_url || value.linkedin_url || socialUrl(value.profile);
};

const nestedPick = (object, paths, fallback = '') => {
  if (!object || typeof object !== 'object') return fallback;
  for (const path of paths) {
    const value = String(path).split('.').reduce((current, key) => current?.[key], object);
    if (hasMeaningfulValue(value)) return value;
  }
  return fallback;
};

const postText = (post) => {
  if (typeof post !== 'object' || !post) return String(post || '');
  return nestedPick(post, [
    'text',
    'content',
    'body',
    'commentary',
    'commentary.text',
    'description',
    'summary',
    'snippet',
    'reshared_post.text',
    'resharedPost.text',
  ], '');
};

const postUrl = (post) => {
  if (typeof post !== 'object' || !post) return '';
  return nestedPick(post, [
    'url',
    'link',
    'post_url',
    'postUrl',
    'permalink',
    'activity_url',
    'activityUrl',
    'share_url',
    'shareUrl',
  ], '');
};

const postDate = (post) => {
  if (typeof post !== 'object' || !post) return '';
  const dateValue = nestedPick(post, [
    'date',
    'date.text',
    'date.value',
    'date.iso',
    'date.timestamp',
    'date.formatted',
    'created_at',
    'createdAt',
    'createdAt.text',
    'createdAt.value',
    'published_at',
    'publishedAt',
    'posted_at',
    'postedAt',
    'postedAt.text',
    'postedAt.value',
    'time',
    'time.text',
    'time.value',
    'timestamp',
    'published.date',
    'published.time',
  ], '');
  if (!dateValue || typeof dateValue !== 'object') return dateValue;
  return pick(dateValue, ['text', 'formatted', 'display', 'value', 'iso', 'date', 'time', 'timestamp'], '');
};

const postAuthor = (post) => {
  if (typeof post !== 'object' || !post) return '';
  const author = nestedPick(post, ['author.name', 'actor.name', 'company.name', 'page.name', 'profile.name'], '');
  const fallback = pick(post, ['author', 'actor', 'publisher'], '');
  if (fallback && typeof fallback === 'object') return valueTitle(fallback, '');
  return author || fallback;
};

const postType = (post) => {
  if (typeof post !== 'object' || !post) return 'Post';
  const type = pick(post, ['type', 'post_type', 'postType', 'content_type', 'contentType'], '');
  if (type) return formatLabel(type);
  const text = postText(post).toLowerCase();
  if (hasMeaningfulValue(pick(post, ['media', 'images', 'image_url', 'imageUrl', 'thumbnail'], ''))) return 'Media Post';
  if (text.includes('hiring') || text.includes('job')) return 'Hiring Signal';
  if (text.includes('webinar') || text.includes('event')) return 'Event';
  return 'Post';
};

const postMediaCount = (post) => {
  if (typeof post !== 'object' || !post) return 0;
  const media = pick(post, ['media', 'images', 'photos', 'attachments'], []);
  if (Array.isArray(media)) return media.filter(hasMeaningfulValue).length;
  if (hasMeaningfulValue(media)) return 1;
  return hasMeaningfulValue(pick(post, ['image_url', 'imageUrl', 'thumbnail'], '')) ? 1 : 0;
};

const mediaUrl = (item) => {
  if (!hasMeaningfulValue(item)) return '';
  if (typeof item === 'string') return isUrl(item) ? item : '';
  if (Array.isArray(item)) return item.map(mediaUrl).find(Boolean) || '';
  return pick(item, ['url', 'src', 'video_url', 'videoUrl', 'image_url', 'imageUrl', 'thumbnail', 'thumbnail_url', 'thumbnailUrl', 'download_url', 'downloadUrl'], '');
};

const isVideoMedia = (item, url) => {
  const type = typeof item === 'object' && item ? String(pick(item, ['type', 'media_type', 'mediaType', 'content_type', 'contentType', 'mime_type', 'mimeType'], '')).toLowerCase() : '';
  const href = String(url || '').split('?')[0].toLowerCase();
  return (
    type.includes('video') ||
    href.includes('/playlist/vid/') ||
    href.includes('/vid/') ||
    href.includes('/video/') ||
    href.includes('/mp4-') ||
    /\.(mp4|webm|mov|m4v|ogg)$/i.test(href)
  );
};

const postMediaItems = (post) => {
  if (typeof post !== 'object' || !post) return [];
  const media = asArray(pick(post, ['media', 'images', 'photos', 'videos', 'attachments'], []));
  const directImage = pick(post, ['image_url', 'imageUrl', 'thumbnail', 'thumbnail_url', 'thumbnailUrl'], '');
  const directVideo = pick(post, ['video_url', 'videoUrl', 'mp4_url', 'mp4Url'], '');
  const items = [...media, directImage, directVideo].filter(hasMeaningfulValue);
  const seen = new Set();
  return items
    .map((item) => {
      const url = mediaUrl(item);
      return {
        item,
        url,
        type: isVideoMedia(item, url) ? 'video' : 'image',
        title: typeof item === 'object' ? valueTitle(item, 'Media') : 'Media',
      };
    })
    .filter(({ url }) => isUrl(url))
    .filter(({ url }) => {
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });
};

const postComments = (post) => {
  if (typeof post !== 'object' || !post) return [];
  return asArray(pick(post, ['comments_list', 'commentsList', 'top_comments', 'topComments', 'recent_comments', 'recentComments', 'comments'], []))
    .filter((comment) => typeof comment === 'object' || String(comment || '').trim())
    .filter((comment) => {
      if (typeof comment !== 'object') return true;
      return hasMeaningfulValue(comment.text || comment.body || comment.comment || comment.message || comment.content || comment.author || comment.actor);
    });
};

const commentText = (comment) => {
  if (typeof comment !== 'object' || !comment) return String(comment || '');
  return pick(comment, ['text', 'body', 'comment', 'message', 'content', 'summary'], '');
};

const commentAuthor = (comment) => {
  if (typeof comment !== 'object' || !comment) return '';
  const author = nestedPick(comment, ['author.name', 'actor.name', 'profile.name', 'person.name'], '');
  const fallback = pick(comment, ['author', 'actor', 'publisher', 'name'], '');
  if (fallback && typeof fallback === 'object') return valueTitle(fallback, '');
  return author || fallback;
};

const metricValue = (post, paths) => {
  const value = nestedPick(post, paths, '');
  if (value && typeof value === 'object') {
    const nestedValue = pick(value, ['count', 'value', 'total'], '');
    if (!nestedValue) return '';
    const nestedNumber = Number(nestedValue);
    return Number.isFinite(nestedNumber) && nestedNumber > 0 ? formatNumber(nestedNumber) : '';
  }
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue <= 0) return '';
  return formatNumber(numberValue);
};

const safeFormattedDate = (value) => {
  if (!hasMeaningfulValue(value)) return '';
  if (typeof value === 'object') {
    const nestedValue = pick(value, ['text', 'formatted', 'display', 'value', 'iso', 'date', 'time', 'timestamp'], '');
    return safeFormattedDate(nestedValue);
  }
  return formatDate(value);
};

const PostMetric = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <Chip
      size="small"
      icon={icon}
      label={`${value} ${label}`}
      sx={{
        height: 30,
        fontWeight: 850,
        color: DARK_TEXT,
        bgcolor: alpha(BRAND_BLUE, 0.045),
        border: `1px solid ${alpha(BRAND_BLUE, 0.08)}`,
        '& .MuiChip-icon': { color: BRAND_BLUE },
      }}
    />
  );
};

const PostMediaAttachment = ({ media, compact = false }) => {
  if (!media?.url) return null;
  const isVideo = media.type === 'video';
  const height = compact ? { xs: 170, sm: 210 } : { xs: 210, sm: 320 };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: compact ? 420 : 640,
        mx: 'auto',
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha('#0A66C2', 0.12)}`,
        bgcolor: '#ffffff',
        position: 'relative',
      }}
    >
      {isVideo ? (
        <Box
          component="video"
          controls
          preload="metadata"
          playsInline
          sx={{
            width: '100%',
            height,
            display: 'block',
            objectFit: 'contain',
            bgcolor: '#08111f',
          }}
        >
          <source src={media.url} type="video/mp4" />
          Your browser cannot play this video preview.
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#f3f6f8' }}>
          <Box
            component="img"
            src={media.url}
            alt={media.title || 'Post media'}
            sx={{
            width: '100%',
              maxHeight: compact ? 260 : 440,
              objectFit: 'cover',
              display: 'block',
              bgcolor: '#f3f6f8',
            }}
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.1,
          py: 0.9,
          borderTop: `1px solid ${alpha(BRAND_BLUE, 0.08)}`,
          bgcolor: '#ffffff',
        }}
      >
        <Chip
          size="small"
          icon={isVideo ? <PlayCircleOutlineIcon /> : <InsertPhotoOutlinedIcon />}
          label={isVideo ? 'Video attachment' : 'Media attachment'}
          sx={{ fontWeight: 850, color: isVideo ? '#0A66C2' : BRAND_BLUE, bgcolor: alpha(isVideo ? '#0A66C2' : BRAND_BLUE, 0.08), '& .MuiChip-icon': { color: isVideo ? '#0A66C2' : BRAND_BLUE } }}
        />
        <Button
          href={media.url}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          endIcon={<OpenInNewIcon />}
          sx={{ textTransform: 'none', fontWeight: 850 }}
        >
          Open media
        </Button>
      </Box>
    </Box>
  );
};

const PostCard = ({ post, platform }) => {
  const isLinkedIn = String(platform || '').toLowerCase().includes('linkedin');
  const text = postText(post);
  const url = postUrl(post);
  const date = postDate(post);
  const displayDate = safeFormattedDate(date);
  const author = postAuthor(post);
  const title = typeof post === 'object' ? valueTitle(post, isLinkedIn ? 'LinkedIn activity' : 'Social post') : 'Social post';
  const mediaItems = postMediaItems(post);
  const mediaCount = mediaItems.length || postMediaCount(post);
  const visibleComments = postComments(post).slice(0, 3);
  const likes = metricValue(post, ['likes', 'like_count', 'likeCount', 'reactions', 'reaction_count', 'reactionCount', 'stats.likes', 'engagement.likes']);
  const comments = metricValue(post, ['comments', 'comment_count', 'commentCount', 'comments_count', 'stats.comments', 'engagement.comments']);
  const reposts = metricValue(post, ['reposts', 'shares', 'share_count', 'shareCount', 'repost_count', 'repostCount', 'stats.shares', 'engagement.shares']);
  const views = metricValue(post, ['views', 'view_count', 'viewCount', 'impressions', 'impression_count', 'impressionCount', 'stats.views', 'engagement.views']);

  return (
    <Card
      sx={{
        height: '100%',
        width: '100%',
        maxWidth: 'none',
        mx: 0,
        p: 0,
        borderRadius: 3,
        border: `1px solid ${alpha(isLinkedIn ? '#0A66C2' : BRAND_BLUE, 0.14)}`,
        boxShadow: isLinkedIn ? `0 14px 30px ${alpha('#0A66C2', 0.08)}` : 'none',
        bgcolor: '#ffffff',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.6,
          py: 1.35,
          borderBottom: `1px solid ${alpha(isLinkedIn ? '#0A66C2' : BRAND_BLUE, 0.09)}`,
          bgcolor: '#ffffff',
        }}
      >
        <Stack direction="row" spacing={1.15} alignItems="flex-start">
          <Avatar sx={{ width: 42, height: 42, bgcolor: isLinkedIn ? '#0A66C2' : alpha(BRAND_BLUE, 0.12), color: isLinkedIn ? '#ffffff' : BRAND_BLUE, flexShrink: 0 }}>
              {isLinkedIn ? <LinkedInIcon fontSize="small" /> : <PublicIcon fontSize="small" />}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {!isLinkedIn ? (
              <Typography sx={{ fontWeight: 950, color: DARK_TEXT, lineHeight: 1.25, wordBreak: 'break-word', ...lineClampSx(1) }}>
                {author && typeof author !== 'object' ? author : formatLabel(platform || 'Social profile')}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.25 }}>
              <Typography variant="caption" sx={{ color: SLATE_TEXT, fontWeight: 750 }}>
                {postType(post)}
              </Typography>
              {displayDate ? (
                <>
                  <Typography variant="caption" sx={{ color: alpha(SLATE_TEXT, 0.7), fontWeight: 900 }}>/</Typography>
                  <Stack direction="row" spacing={0.35} alignItems="center">
                    <CalendarMonthIcon sx={{ fontSize: 17, color: BRAND_BLUE }} />
                    <Typography sx={{ color: DARK_TEXT, fontSize: '0.86rem', fontWeight: 900 }}>{displayDate}</Typography>
                  </Stack>
                </>
              ) : null}
            </Stack>
            {!isLinkedIn && title && title !== author && title !== 'LinkedIn activity' && title !== 'Social post' ? (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: BRAND_BLUE, fontWeight: 850, ...lineClampSx(1) }}>
                {title}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: 1.7 }}>
        <Typography
          variant="body2"
          sx={{
            color: DARK_TEXT,
            fontSize: isLinkedIn ? '0.98rem' : '0.92rem',
            lineHeight: 1.78,
            fontWeight: 500,
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            ...lineClampSx(isLinkedIn ? 7 : 3),
          }}
        >
          {text || title}
        </Typography>

        {mediaItems.length ? (
          <Stack spacing={1.1} sx={{ mt: 1.4 }}>
            {mediaItems.slice(0, 2).map((media, index) => (
              <PostMediaAttachment key={`${media.url}-${index}`} media={media} compact={isLinkedIn} />
            ))}
          </Stack>
        ) : null}

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.35 }}>
          <Chip
            size="small"
            icon={<ArticleOutlinedIcon />}
            label={postType(post)}
            sx={{ fontWeight: 850, color: BRAND_BLUE, bgcolor: alpha(BRAND_BLUE, 0.08), '& .MuiChip-icon': { color: BRAND_BLUE } }}
          />
          {mediaCount ? (
            <Chip
              size="small"
              icon={<InsertPhotoOutlinedIcon />}
              label={`${mediaCount} media item${mediaCount === 1 ? '' : 's'}`}
              sx={{ fontWeight: 850, '& .MuiChip-icon': { color: BRAND_BLUE } }}
            />
          ) : null}
        </Stack>

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
          <PostMetric icon={<FavoriteBorderIcon />} label="reactions" value={likes} />
          <PostMetric icon={<ChatBubbleOutlineIcon />} label="comments" value={comments} />
          <PostMetric icon={<RepeatIcon />} label="shares" value={reposts} />
          <PostMetric icon={<VisibilityOutlinedIcon />} label="views" value={views} />
        </Stack>

        {visibleComments.length ? (
          <Box sx={{ mt: 1.35, p: 1.2, borderRadius: 3, bgcolor: alpha(BRAND_BLUE, 0.035), border: `1px solid ${alpha(BRAND_BLUE, 0.08)}` }}>
            <Typography sx={{ mb: 0.9, fontSize: '0.74rem', fontWeight: 900, color: BRAND_BLUE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Visible comments
            </Typography>
            <Stack spacing={0.9}>
              {visibleComments.map((comment, index) => {
                const authorName = commentAuthor(comment);
                const body = commentText(comment);
                if (!body && !authorName) return null;
                return (
                  <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(BRAND_BLUE, 0.1), color: BRAND_BLUE, fontSize: '0.74rem', fontWeight: 900 }}>
                      {(authorName || 'C').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1, p: 1, borderRadius: 2.5, bgcolor: '#ffffff', border: `1px solid ${alpha(BRAND_BLUE, 0.07)}` }}>
                      {authorName ? <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: DARK_TEXT }}>{authorName}</Typography> : null}
                      {body ? <Typography variant="body2" sx={{ mt: authorName ? 0.35 : 0, color: SLATE_TEXT, lineHeight: 1.65, ...lineClampSx(2) }}>{body}</Typography> : null}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ) : null}

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.45 }}>
          {url ? (
            <Button
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="contained"
              endIcon={<OpenInNewIcon />}
              sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900, bgcolor: isLinkedIn ? '#0A66C2' : BRAND_BLUE }}
            >
              Open post
            </Button>
          ) : null}
        </Stack>
      </Box>
    </Card>
  );
};

const SocialPostGrid = ({ posts, platform, isLinkedIn, hasDeepProfile }) => {
  const pageSize = isLinkedIn ? 6 : hasDeepProfile ? 6 : 3;
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(posts.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const visiblePosts = posts.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{
          mb: 1.2,
          p: 1.2,
          borderRadius: 3,
          bgcolor: isLinkedIn ? alpha('#0A66C2', 0.045) : alpha(BRAND_BLUE, 0.035),
          border: `1px solid ${alpha(isLinkedIn ? '#0A66C2' : BRAND_BLUE, 0.1)}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '0.76rem', fontWeight: 900, color: isLinkedIn ? '#0A66C2' : BRAND_BLUE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {isLinkedIn ? 'LinkedIn post intelligence' : 'Recent activity'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.35, color: SLATE_TEXT }}>
            Showing {visiblePosts.length} of {posts.length} discovered post{posts.length === 1 ? '' : 's'}.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          {isLinkedIn ? (
            <Chip
              icon={<LinkedInIcon />}
              label="Company activity grid"
              sx={{ fontWeight: 900, color: '#0A66C2', bgcolor: '#ffffff', '& .MuiChip-icon': { color: '#0A66C2' } }}
            />
          ) : null}
          {pageCount > 1 ? (
            <Stack direction="row" spacing={0.75}>
              <Button
                size="small"
                variant="outlined"
                disabled={currentPage === 0}
                onClick={() => setPage((previous) => Math.max(0, previous - 1))}
                sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900 }}
              >
                Previous
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={currentPage >= pageCount - 1}
                onClick={() => setPage((previous) => Math.min(pageCount - 1, previous + 1))}
                sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900 }}
              >
                Next
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </Stack>

      <Grid container spacing={1.35}>
        {visiblePosts.map((post, index) => (
          <Grid item xs={12} sm={6} lg={isLinkedIn ? 4 : hasDeepProfile ? 6 : 12} key={`${currentPage}-${index}`}>
            <PostCard post={post} platform={platform} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const SocialProfiles = ({ social }) => {
  const entries = Object.entries(social || {}).filter(([, value]) => hasMeaningfulValue(value));
  if (!entries.length) return <EmptyState title="No social media data returned" />;

  return (
    <Grid container spacing={2}>
      {entries.map(([platform, value]) => {
        const { profile, posts, description, url, fields, hasDeepProfile, normalizedPlatform } = socialPlatformData(platform, value);
        const isLinkedIn = normalizedPlatform.includes('linkedin');
        return (
          <Grid item xs={12} md={hasDeepProfile ? 12 : 6} xl={hasDeepProfile ? 12 : 4} key={platform}>
            <SectionCard eyebrow="Social Platform" title={formatLabel(platform)} sx={{ height: '100%' }}>
              <Stack spacing={1.4}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar sx={{ bgcolor: alpha(BRAND_BLUE, 0.1), color: BRAND_BLUE }}><PublicIcon /></Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: DARK_TEXT }}>{formatLabel(platform)}</Typography>
                      {url ? <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{url}</Link> : null}
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip label={`${posts.length} post${posts.length === 1 ? '' : 's'}`} sx={{ fontWeight: 900, color: BRAND_BLUE, bgcolor: alpha(BRAND_BLUE, 0.08) }} />
                    {hasMeaningfulValue(profile) ? <DetailButton item={profile} title={`${formatLabel(platform)} profile`} subtitle="Full social profile data" /> : null}
                  </Stack>
                </Stack>

                {description ? (
                  <Typography variant="body2" sx={{ color: SLATE_TEXT, lineHeight: 1.75, ...lineClampSx(hasDeepProfile ? 3 : 2) }}>
                    {description}
                  </Typography>
                ) : null}

                {fields.length ? (
                  <Grid container spacing={1}>
                    {fields.slice(0, hasDeepProfile ? 8 : 4).map(([label, entry]) => (
                      <Grid item xs={12} sm={6} lg={hasDeepProfile ? 3 : 6} key={label}>
                        <Box sx={{ p: 1.1, borderRadius: 2.5, border: `1px solid ${alpha(BRAND_BLUE, 0.08)}`, bgcolor: alpha(BRAND_BLUE, 0.025), minHeight: 70 }}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: BRAND_BLUE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
                          {isUrl(entry) ? (
                            <Link href={String(entry)} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', mt: 0.45, fontWeight: 800, wordBreak: 'break-word' }}>{String(entry)}</Link>
                          ) : (
                            <Typography variant="body2" sx={{ mt: 0.45, fontWeight: 850, color: DARK_TEXT, wordBreak: 'break-word', ...lineClampSx(2) }}>
                              {typeof entry === 'object' ? valueTitle(entry, formatAddress(entry) || 'Available') : String(entry)}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" sx={{ color: SLATE_TEXT }}>No profile summary fields returned.</Typography>
                )}

                {posts.length ? (
                  <SocialPostGrid posts={posts} platform={platform} isLinkedIn={isLinkedIn} hasDeepProfile={hasDeepProfile} />
                ) : (
                  <EmptyState title="No recent posts found" description="This platform returned a profile reference but no recent posts." />
                )}
                <SourceBadges refs={pick(value, ['source_refs', 'sourceRefs', 'sources'], [])} />
              </Stack>
            </SectionCard>
          </Grid>
        );
      })}
    </Grid>
  );
};

export const SocialSummaryProfiles = ({ social }) => {
  const entries = Object.entries(social || {}).filter(([, value]) => hasMeaningfulValue(value));
  if (!entries.length) return <EmptyState title="No social media data returned" />;

  return (
    <Grid container spacing={1.5}>
      {entries.map(([platform, value]) => {
        const { profile, posts, description, url, fields } = socialPlatformData(platform, value);
        const stats = profileStats(profile);
        const followers = pick(stats, ['follower_count', 'followers', 'followerCount'], '');
        const employees = pick(stats, ['employee_count', 'employeeCount'], '');
        return (
          <Grid item xs={12} md={6} xl={4} key={platform}>
            <Card sx={{ height: '100%', borderRadius: 3, border: `1px solid ${alpha(BRAND_BLUE, 0.12)}`, boxShadow: 'none', overflow: 'hidden' }}>
              <CardContent sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                  <Avatar sx={{ bgcolor: alpha(BRAND_BLUE, 0.1), color: BRAND_BLUE }}><PublicIcon /></Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 900, color: DARK_TEXT }}>{formatLabel(platform)}</Typography>
                    {url ? <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{url}</Link> : null}
                    {description ? (
                      <Typography variant="body2" sx={{ mt: 0.8, color: SLATE_TEXT, lineHeight: 1.65, ...lineClampSx(3) }}>
                        {description}
                      </Typography>
                    ) : null}
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
                      <Chip size="small" label={`${posts.length} posts`} sx={{ fontWeight: 850, color: BRAND_BLUE, bgcolor: alpha(BRAND_BLUE, 0.08) }} />
                      {followers ? <Chip size="small" label={`${followers} followers`} sx={{ fontWeight: 850 }} /> : null}
                      {employees ? <Chip size="small" label={`${employees} employees`} sx={{ fontWeight: 850 }} /> : null}
                    </Stack>
                    {fields.length ? (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1.1, color: SLATE_TEXT, lineHeight: 1.6, ...lineClampSx(2) }}>
                        {fields.slice(0, 3).map(([label, entry]) => `${label}: ${typeof entry === 'object' ? valueTitle(entry, 'Available') : entry}`).join(' | ')}
                      </Typography>
                    ) : null}
                    {hasMeaningfulValue(profile) ? (
                      <Box sx={{ mt: 1.2 }}>
                        <DetailButton item={profile} title={`${formatLabel(platform)} profile`} subtitle="Full social profile data" />
                      </Box>
                    ) : null}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export const NewsArticles = ({ news }) => {
  const summary = pick(news, ['summary'], {});
  const articles = asArray(pick(news, ['articles', 'news_articles', 'items'], []));
  const topics = asArray(pick(summary, ['topics'], pick(news, ['topics', 'summary_topics'], []))).filter(hasMeaningfulValue);
  const articleCount = pick(summary, ['article_count', 'articleCount'], articles.length);
  const latest = pick(summary, ['latest_article_date', 'latestArticleDate'], pick(news, ['latest_article_date', 'latestArticleDate'], ''));

  return (
    <SectionCard eyebrow="News" title="Recent public articles">
      <Grid container spacing={1.3} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}><Chip label={`${articleCount || 0} articles`} sx={{ width: '100%', height: 42, fontWeight: 900, bgcolor: alpha(BRAND_BLUE, 0.08), color: BRAND_BLUE }} /></Grid>
        <Grid item xs={12} sm={4}><Chip label={latest ? `Latest: ${formatDate(latest)}` : 'Latest: Unknown'} sx={{ width: '100%', height: 42, fontWeight: 900 }} /></Grid>
        <Grid item xs={12} sm={4}><Chip label={`${topics.length} topics`} sx={{ width: '100%', height: 42, fontWeight: 900 }} /></Grid>
      </Grid>
      {topics.length ? <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>{topics.map((topic) => <Chip key={topic} size="small" label={topic} sx={{ fontWeight: 800 }} />)}</Stack> : null}
      {articles.length ? (
        <Stack spacing={1.3}>
          {articles.map((article, index) => {
            const title = valueTitle(article, 'Untitled article');
            const url = pick(article, ['url', 'link'], '');
            const publisher = pick(article, ['publisher', 'source', 'site'], '');
            const date = pick(article, ['date', 'published_at', 'publishedAt'], '');
            const snippet = pick(article, ['snippet', 'summary', 'description'], '');
            const articleTopics = asArray(pick(article, ['topics', 'tags'], []));
            return (
              <Card key={`${title}-${index}`} sx={{ borderRadius: 3, border: `1px solid ${alpha(BRAND_BLUE, 0.1)}`, boxShadow: 'none' }}>
                <CardContent sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Avatar sx={{ bgcolor: alpha(BRAND_BLUE, 0.1), color: BRAND_BLUE }}><NewspaperIcon /></Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 900, color: DARK_TEXT, wordBreak: 'break-word' }}>{title}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: SLATE_TEXT }}>{[publisher, formatDate(date)].filter(Boolean).join(' | ')}</Typography>
                      {snippet ? <Typography variant="body2" sx={{ mt: 0.8, color: SLATE_TEXT, lineHeight: 1.7 }}>{truncateText(snippet, 240)}</Typography> : null}
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
                        {articleTopics.map((topic) => <Chip key={topic} size="small" label={topic} />)}
                        {url ? <Button href={url} target="_blank" rel="noopener noreferrer" size="small" endIcon={<OpenInNewIcon />} sx={{ textTransform: 'none', fontWeight: 800 }}>Open article</Button> : null}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <EmptyState title="No recent news articles found" description="The OSINT agent did not return recent article matches for this company." />
      )}
      <SourceBadges refs={pick(news, ['source_refs', 'sourceRefs', 'sources'], [])} />
    </SectionCard>
  );
};

export const JobPostings = ({ jobs }) => {
  const summary = pick(jobs, ['summary'], {});
  const postings = asArray(pick(jobs, ['postings', 'job_postings', 'jobs'], []));
  const departments = asArray(pick(summary, ['departments'], pick(jobs, ['departments'], [])));
  const locations = asArray(pick(summary, ['locations'], pick(jobs, ['locations'], [])));
  const titles = asArray(pick(summary, ['common_titles', 'commonTitles'], pick(jobs, ['common_titles', 'commonTitles'], [])));
  const techSignals = asArray(pick(summary, ['tech_signals', 'techSignals'], pick(jobs, ['tech_signals', 'techSignals'], [])));
  const openRoles = pick(summary, ['open_roles_count', 'openRolesCount'], pick(jobs, ['open_roles', 'openRoles'], postings.length));

  const chipGroup = (label, values, accent = BRAND_BLUE) => (
    <Box>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, textTransform: 'uppercase', color: accent }}>{label}</Typography>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.8 }}>
        {values.filter(hasMeaningfulValue).length ? values.filter(hasMeaningfulValue).map((item) => <Chip key={String(item)} size="small" label={String(item)} sx={{ fontWeight: 800 }} />) : <Typography variant="body2" sx={{ color: SLATE_TEXT }}>No values returned.</Typography>}
      </Stack>
    </Box>
  );

  return (
    <SectionCard eyebrow="Hiring Intelligence" title="Jobs and technology signals">
      <Grid container spacing={2}>
        <Grid item xs={12} lg={4}>
          <Card sx={{ ...shellCardSx, boxShadow: 'none', borderTop: `3px solid ${SUCCESS_GREEN}` }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Avatar sx={{ bgcolor: alpha(SUCCESS_GREEN, 0.1), color: SUCCESS_GREEN }}><WorkIcon /></Avatar>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: SUCCESS_GREEN, textTransform: 'uppercase' }}>Open Roles</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: DARK_TEXT }}>{openRoles || 0}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {chipGroup('Departments', departments, SUCCESS_GREEN)}
            {chipGroup('Locations', locations, '#7C3AED')}
            {chipGroup('Common Titles', titles, BRAND_BLUE)}
            {chipGroup('Tech Signals', techSignals, WARNING_AMBER)}
          </Stack>
        </Grid>
        <Grid item xs={12} lg={8}>
          {postings.length ? (
            <Stack spacing={1.2}>
              {postings.map((job, index) => {
                const title = valueTitle(job, 'Untitled role');
                const url = pick(job, ['url', 'link'], '');
                const department = pick(job, ['department', 'team'], '');
                const location = pick(job, ['location', 'locations'], '');
                return (
                  <Card key={`${title}-${index}`} sx={{ p: 1.7, borderRadius: 3, border: `1px solid ${alpha(SUCCESS_GREEN, 0.12)}`, boxShadow: 'none' }}>
                    <Typography sx={{ fontWeight: 900, color: DARK_TEXT }}>{title}</Typography>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                      {department ? <Chip size="small" label={department} sx={{ fontWeight: 800, color: SUCCESS_GREEN, bgcolor: alpha(SUCCESS_GREEN, 0.08) }} /> : null}
                      {location ? <Chip size="small" label={typeof location === 'string' ? location : formatAddress(location)} /> : null}
                      {url ? <Button href={url} target="_blank" rel="noopener noreferrer" size="small" endIcon={<OpenInNewIcon />} sx={{ textTransform: 'none', fontWeight: 800 }}>Open posting</Button> : null}
                    </Stack>
                    <DetailButton item={job} title={title} subtitle="Job posting" />
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <EmptyState title="No job postings returned" description="Hiring summary data may still be available even when no individual posting records are present." />
          )}
        </Grid>
      </Grid>
      <SourceBadges refs={pick(jobs, ['source_refs', 'sourceRefs', 'sources'], [])} />
    </SectionCard>
  );
};
